<template>
  <div class="baa-signing-page">
    <!-- Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-icon">
          <i class="fas fa-file-signature"></i>
        </div>
        <div class="header-text">
          <h1>Business Associate Agreement</h1>
          <p class="subtitle">HIPAA Compliance Documentation</p>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading BAA information...</p>
    </div>

    <!-- Already Signed State -->
    <div v-else-if="baaStatus?.baa_status === 'SIGNED'" class="already-signed-container">
      <div class="success-card">
        <div class="success-icon">
          <i class="fas fa-check-circle"></i>
        </div>
        <h2>BAA Already Signed</h2>
        <p class="signed-info">
          This Business Associate Agreement was signed by 
          <strong>{{ baaStatus.baa_signatory_name }}</strong>
          on <strong>{{ formatDate(baaStatus.baa_signed_at) }}</strong>.
        </p>
        <div class="signed-details">
          <div class="detail-item">
            <span class="label">Signatory:</span>
            <span class="value">{{ baaStatus.baa_signatory_name }} ({{ baaStatus.baa_signatory_title }})</span>
          </div>
          <div class="detail-item">
            <span class="label">Email:</span>
            <span class="value">{{ baaStatus.baa_signatory_email }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Expires:</span>
            <span class="value">{{ formatDate(baaStatus.baa_expires_at) }}</span>
          </div>
        </div>
        <router-link to="/settings/organization" class="btn btn-primary">
          <i class="fas fa-arrow-left"></i> Back to Organization Settings
        </router-link>
      </div>
    </div>

    <!-- Signing Form -->
    <div v-else class="signing-container">
      <!-- Progress Steps -->
      <div class="progress-steps">
        <div class="step" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
          <div class="step-number">1</div>
          <span>Review Agreement</span>
        </div>
        <div class="step-connector"></div>
        <div class="step" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
          <div class="step-number">2</div>
          <span>Your Information</span>
        </div>
        <div class="step-connector"></div>
        <div class="step" :class="{ active: currentStep >= 3 }">
          <div class="step-number">3</div>
          <span>Sign & Submit</span>
        </div>
      </div>

      <!-- Step 1: Review Agreement -->
      <div v-if="currentStep === 1" class="step-content">
        <div class="agreement-section">
          <h2>Business Associate Agreement</h2>
          <p class="agreement-intro">
            Please review the following Business Associate Agreement carefully. 
            This agreement is required for HIPAA compliance when handling Protected Health Information (PHI).
          </p>

          <div class="entity-selection">
            <label>Select Covered Entity:</label>
            <div class="entity-options">
              <label v-for="entity in coveredEntities" :key="entity.name" class="entity-option">
                <input type="radio" v-model="selectedEntity" :value="entity.name">
                <span class="entity-name">{{ entity.name }}</span>
              </label>
            </div>
          </div>

          <div class="agreement-document">
            <div class="document-header">
              <h3>BUSINESS ASSOCIATE AGREEMENT</h3>
              <p class="effective-date">Effective Date: {{ todayDate }}</p>
            </div>

            <div class="parties-section">
              <p>
                This Business Associate Agreement ("Agreement") is entered into by and between:
              </p>
              <p class="party">
                <strong>Covered Entity:</strong> {{ selectedEntity || '[Select Above]' }}
              </p>
              <p class="party">
                <strong>Business Associate:</strong> {{ baaStatus?.organization_name || 'Your Organization' }}
              </p>
            </div>

            <div class="agreement-body">
              <h4>1. DEFINITIONS</h4>
              <p>
                Terms used, but not otherwise defined, in this Agreement shall have the same meaning 
                as those terms in the HIPAA Rules, including but not limited to: "Breach," "Business Associate," 
                "Covered Entity," "Designated Record Set," "Disclosure," "Electronic Protected Health Information," 
                "Individual," "Protected Health Information," "Required By Law," "Secretary," "Security Incident," 
                "Subcontractor," and "Use."
              </p>

              <h4>2. OBLIGATIONS AND ACTIVITIES OF BUSINESS ASSOCIATE</h4>
              <p>Business Associate agrees to:</p>
              <ul>
                <li>Not use or disclose Protected Health Information other than as permitted or required by this Agreement or as Required by Law;</li>
                <li>Use appropriate safeguards and comply with Subpart C of 45 C.F.R. Part 164 to prevent use or disclosure of Protected Health Information other than as provided for by this Agreement;</li>
                <li>Report to Covered Entity any use or disclosure of Protected Health Information not provided for by this Agreement of which it becomes aware, including breaches of Unsecured Protected Health Information;</li>
                <li>Ensure that any Subcontractors that create, receive, maintain, or transmit Protected Health Information on behalf of the Business Associate agree to the same restrictions and conditions;</li>
                <li>Make available Protected Health Information in accordance with 45 C.F.R. § 164.524;</li>
                <li>Make Protected Health Information available for amendment and incorporate any amendments in accordance with 45 C.F.R. § 164.526;</li>
                <li>Make internal practices available to the Secretary for purposes of determining Covered Entity's compliance with HIPAA Rules;</li>
                <li>Maintain and provide access to an accounting of disclosures in accordance with 45 C.F.R. § 164.528.</li>
              </ul>

              <h4>3. PERMITTED USES AND DISCLOSURES</h4>
              <p>
                Business Associate may only use or disclose Protected Health Information as necessary to 
                perform services on behalf of Covered Entity as specified in this Agreement, provided that 
                such use or disclosure would not violate the HIPAA Rules if done by Covered Entity.
              </p>

              <h4>4. TERM AND TERMINATION</h4>
              <p>
                This Agreement shall be effective as of the date signed below and shall terminate when 
                all of the Protected Health Information provided by Covered Entity to Business Associate 
                is destroyed or returned to Covered Entity. This Agreement shall automatically terminate 
                upon termination of the underlying service relationship.
              </p>

              <h4>5. BREACH NOTIFICATION</h4>
              <p>
                Business Associate shall report to Covered Entity any Breach of Unsecured Protected Health 
                Information within 60 days of discovery. Such report shall include: (i) identification of 
                each Individual affected; (ii) a description of what happened; (iii) a description of the 
                types of PHI involved; (iv) steps the Individual should take to protect themselves; and 
                (v) what Business Associate is doing to investigate, mitigate, and prevent future occurrences.
              </p>

              <h4>6. MISCELLANEOUS</h4>
              <p>
                This Agreement shall be governed by and construed in accordance with applicable federal law. 
                Any ambiguity shall be resolved to permit compliance with the HIPAA Rules.
              </p>
            </div>
          </div>

          <div class="step-actions">
            <button @click="currentStep = 2" class="btn btn-primary" :disabled="!selectedEntity">
              I Have Reviewed the Agreement <i class="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Step 2: Signatory Information -->
      <div v-if="currentStep === 2" class="step-content">
        <div class="signatory-section">
          <h2>Signatory Information</h2>
          <p class="section-intro">
            Please provide the information of the authorized representative who will sign this agreement.
          </p>

          <form @submit.prevent="currentStep = 3" class="signatory-form">
            <div class="form-group">
              <label for="signatoryName">Full Legal Name *</label>
              <input 
                type="text" 
                id="signatoryName" 
                v-model="signatoryName" 
                required 
                placeholder="e.g., John Smith"
                class="form-control"
              >
            </div>

            <div class="form-group">
              <label for="signatoryTitle">Title / Position *</label>
              <input 
                type="text" 
                id="signatoryTitle" 
                v-model="signatoryTitle" 
                required 
                placeholder="e.g., Chief Compliance Officer"
                class="form-control"
              >
            </div>

            <div class="form-group">
              <label for="signatoryEmail">Email Address *</label>
              <input 
                type="email" 
                id="signatoryEmail" 
                v-model="signatoryEmail" 
                required 
                placeholder="e.g., compliance@yourcompany.com"
                class="form-control"
              >
            </div>

            <div class="authorization-notice">
              <i class="fas fa-info-circle"></i>
              <p>
                By proceeding, you confirm that you are authorized to sign legal agreements 
                on behalf of <strong>{{ baaStatus?.organization_name }}</strong>.
              </p>
            </div>

            <div class="step-actions">
              <button type="button" @click="currentStep = 1" class="btn btn-outline">
                <i class="fas fa-arrow-left"></i> Back
              </button>
              <button 
                type="submit" 
                class="btn btn-primary"
                :disabled="!signatoryName || !signatoryTitle || !signatoryEmail"
              >
                Continue to Signature <i class="fas fa-arrow-right"></i>
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Step 3: Sign & Submit -->
      <div v-if="currentStep === 3" class="step-content">
        <div class="signature-section">
          <h2>Electronic Signature</h2>
          <p class="section-intro">
            Review the details below and check the box to electronically sign this Business Associate Agreement.
          </p>

          <div class="signature-summary">
            <div class="summary-item">
              <span class="label">Covered Entity:</span>
              <span class="value">{{ selectedEntity }}</span>
            </div>
            <div class="summary-item">
              <span class="label">Business Associate:</span>
              <span class="value">{{ baaStatus?.organization_name }}</span>
            </div>
            <div class="summary-item">
              <span class="label">Signatory:</span>
              <span class="value">{{ signatoryName }} ({{ signatoryTitle }})</span>
            </div>
            <div class="summary-item">
              <span class="label">Email:</span>
              <span class="value">{{ signatoryEmail }}</span>
            </div>
            <div class="summary-item">
              <span class="label">Date:</span>
              <span class="value">{{ todayDate }}</span>
            </div>
          </div>

          <div class="signature-box">
            <label class="checkbox-container">
              <input type="checkbox" v-model="agreeToTerms">
              <span class="checkmark"></span>
              <span class="checkbox-text">
                I, <strong>{{ signatoryName }}</strong>, hereby acknowledge that I have read and understand 
                this Business Associate Agreement, and I am authorized to sign this agreement on behalf of 
                <strong>{{ baaStatus?.organization_name }}</strong>. By checking this box and clicking 
                "Sign Agreement," I am electronically signing this document with the same legal effect as 
                a handwritten signature.
              </span>
            </label>
          </div>

          <div v-if="error" class="error-message">
            <i class="fas fa-exclamation-circle"></i>
            {{ error }}
          </div>

          <div class="step-actions">
            <button type="button" @click="currentStep = 2" class="btn btn-outline" :disabled="signing">
              <i class="fas fa-arrow-left"></i> Back
            </button>
            <button 
              @click="signAgreement" 
              class="btn btn-success btn-sign"
              :disabled="!agreeToTerms || signing"
            >
              <span v-if="signing">
                <i class="fas fa-spinner fa-spin"></i> Signing...
              </span>
              <span v-else>
                <i class="fas fa-file-signature"></i> Sign Agreement
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Success State -->
      <div v-if="signedSuccessfully" class="success-overlay">
        <div class="success-modal">
          <div class="success-icon-large">
            <i class="fas fa-check-circle"></i>
          </div>
          <h2>Agreement Signed Successfully!</h2>
          <p>
            The Business Associate Agreement has been electronically signed and is now in effect.
            A copy has been recorded for your organization's records.
          </p>
          <router-link to="/settings/organization" class="btn btn-primary">
            Return to Organization Settings
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import api from '@/services/api';

const loading = ref(true);
const signing = ref(false);
const error = ref(null);
const baaStatus = ref(null);
const currentStep = ref(1);
const signedSuccessfully = ref(false);

// Form fields
const selectedEntity = ref('');
const signatoryName = ref('');
const signatoryTitle = ref('');
const signatoryEmail = ref('');
const agreeToTerms = ref(false);

// Covered entities
const coveredEntities = ref([
  { name: 'Actionable Document Intelligence LLC', shortName: 'ADI' },
  { name: 'Praetorian, LLC', shortName: 'Praetorian' },
]);

// Today's date formatted
const todayDate = computed(() => {
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
});

// Load BAA status on mount
onMounted(async () => {
  try {
    const response = await api.get('/users/baa/status/');
    baaStatus.value = response.data;
    
    // Pre-fill email if user is logged in
    const userResponse = await api.get('/users/me/');
    if (userResponse.data?.email) {
      signatoryEmail.value = userResponse.data.email;
    }
    if (userResponse.data?.first_name && userResponse.data?.last_name) {
      signatoryName.value = `${userResponse.data.first_name} ${userResponse.data.last_name}`;
    }
  } catch (err) {
    console.error('Error loading BAA status:', err);
    error.value = 'Failed to load BAA information. Please try again.';
  } finally {
    loading.value = false;
  }
});

// Format date helper
function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Sign the agreement
async function signAgreement() {
  if (!agreeToTerms.value) {
    error.value = 'You must agree to the terms to sign this agreement.';
    return;
  }

  signing.value = true;
  error.value = null;

  try {
    const response = await api.post('/users/baa/sign/', {
      signatory_name: signatoryName.value,
      signatory_title: signatoryTitle.value,
      signatory_email: signatoryEmail.value,
      agree_to_terms: agreeToTerms.value,
      covered_entity: selectedEntity.value,
    });

    if (response.data.success) {
      signedSuccessfully.value = true;
    } else {
      error.value = response.data.error || 'An error occurred. Please try again.';
    }
  } catch (err) {
    console.error('Error signing BAA:', err);
    error.value = err.response?.data?.error || 'Failed to sign agreement. Please try again.';
  } finally {
    signing.value = false;
  }
}
</script>

<style scoped>
.baa-signing-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem;
}

.page-header {
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.header-text h1 {
  margin: 0;
  font-size: 1.75rem;
  color: #1f2937;
}

.header-text .subtitle {
  margin: 0.25rem 0 0;
  color: #6b7280;
}

/* Progress Steps */
.progress-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f9fafb;
  border-radius: 12px;
}

.step {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #9ca3af;
}

.step.active {
  color: #3b82f6;
}

.step.completed {
  color: #10b981;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

.step.active .step-number {
  background: #3b82f6;
  color: white;
}

.step.completed .step-number {
  background: #10b981;
  color: white;
}

.step-connector {
  width: 60px;
  height: 2px;
  background: #e5e7eb;
  margin: 0 1rem;
}

/* Agreement Document */
.agreement-document {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 2rem;
  max-height: 400px;
  overflow-y: auto;
  margin: 1.5rem 0;
  font-size: 0.9rem;
  line-height: 1.6;
}

.document-header {
  text-align: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
}

.document-header h3 {
  margin: 0;
  font-size: 1.25rem;
}

.effective-date {
  color: #6b7280;
  margin-top: 0.5rem;
}

.parties-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.party {
  margin: 0.5rem 0;
}

.agreement-body h4 {
  color: #1f2937;
  margin: 1.5rem 0 0.5rem;
  font-size: 1rem;
}

.agreement-body ul {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.agreement-body li {
  margin: 0.5rem 0;
}

/* Entity Selection */
.entity-selection {
  margin-bottom: 1.5rem;
}

.entity-selection label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.entity-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.entity-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.entity-option:hover {
  border-color: #3b82f6;
  background: #f0f9ff;
}

.entity-option input:checked + .entity-name {
  font-weight: 600;
  color: #3b82f6;
}

.entity-option input:checked ~ .entity-name {
  font-weight: 600;
}

.entity-option:has(input:checked) {
  border-color: #3b82f6;
  background: #eff6ff;
}

/* Form Styles */
.signatory-form {
  max-width: 500px;
  margin: 0 auto;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #374151;
}

.form-control {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.authorization-notice {
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  background: #fef3c7;
  border-radius: 8px;
  color: #92400e;
  margin-bottom: 1.5rem;
}

.authorization-notice i {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.authorization-notice p {
  margin: 0;
}

/* Signature Section */
.signature-summary {
  background: #f9fafb;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-item .label {
  color: #6b7280;
}

.summary-item .value {
  font-weight: 500;
}

.signature-box {
  background: #fff;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.checkbox-container {
  display: flex;
  gap: 1rem;
  cursor: pointer;
}

.checkbox-container input {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  cursor: pointer;
}

.checkbox-text {
  line-height: 1.6;
  color: #374151;
}

/* Buttons */
.step-actions {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 2rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-size: 1rem;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-success {
  background: #10b981;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #059669;
}

.btn-outline {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-outline:hover {
  background: #f9fafb;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-sign {
  padding: 1rem 2rem;
  font-size: 1.1rem;
}

/* Error Message */
.error-message {
  background: #fef2f2;
  color: #dc2626;
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

/* Success States */
.already-signed-container,
.success-overlay {
  padding: 2rem;
}

.success-card {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.success-icon,
.success-icon-large {
  width: 80px;
  height: 80px;
  background: #d1fae5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.success-icon i,
.success-icon-large i {
  font-size: 2.5rem;
  color: #10b981;
}

.signed-details {
  background: #f9fafb;
  border-radius: 8px;
  padding: 1rem;
  margin: 1.5rem 0;
  text-align: left;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
}

.detail-item .label {
  color: #6b7280;
}

/* Success Overlay */
.success-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.success-modal {
  background: white;
  border-radius: 16px;
  padding: 3rem;
  max-width: 500px;
  text-align: center;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Loading */
.loading-container {
  text-align: center;
  padding: 3rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .baa-signing-page {
    padding: 1rem;
  }
  
  .progress-steps {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .step-connector {
    display: none;
  }
  
  .step-actions {
    flex-direction: column;
  }
  
  .agreement-document {
    max-height: 300px;
  }
}
</style>

